import React, { useCallback, useEffect, useState } from 'react';
import { supabaseAdmin, isSupabaseProxyModeActive } from '../config/supabase';
import toast from 'react-hot-toast';
import { Network } from 'lucide-react';
import {
  SUPABASE_PROXY_SETTING_KEY,
  isDirectEnvAvailable,
  isProxyEnvAvailable,
  readProxyEnabledFromStorage,
  writeProxyEnabledToStorage,
} from '../utils/supabaseProxySetting';
import { upsertAppSettingViaProxy } from '../utils/supabaseProxyApi';
import './SupabaseProxyConfig.css';

async function persistProxySettingToDatabase(enabled) {
  const payload = { enabled: enabled === true };

  if (isProxyEnvAvailable()) {
    const viaProxy = await upsertAppSettingViaProxy(SUPABASE_PROXY_SETTING_KEY, payload);
    if (!viaProxy.error) return { ok: true, via: 'proxy' };
  }

  const { error } = await supabaseAdmin.from('app_settings').upsert(
    {
      setting_key: SUPABASE_PROXY_SETTING_KEY,
      setting_value: payload,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'setting_key' }
  );

  if (error) return { ok: false, error };
  return { ok: true, via: 'client' };
}

export default function SupabaseProxyConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proxyEnabled, setProxyEnabled] = useState(true);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseAdmin
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', SUPABASE_PROXY_SETTING_KEY)
        .maybeSingle();

      if (error) throw error;

      const enabled = data?.setting_value?.enabled;
      if (enabled === true || enabled === false) {
        setProxyEnabled(enabled);
        writeProxyEnabledToStorage(enabled);
      } else {
        setProxyEnabled(readProxyEnabledFromStorage());
      }
    } catch (err) {
      console.error('Error loading supabase proxy config:', err);
      toast.error('Błąd ładowania ustawienia proxy');
      setProxyEnabled(readProxyEnabledFromStorage());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (proxyEnabled && !isProxyEnvAvailable()) {
      toast.error('Nie można włączyć proxy: brak REACT_APP_SUPABASE_PROXY_URL w .env');
      return;
    }
    if (!proxyEnabled && !isDirectEnvAvailable()) {
      toast.error(
        'Nie można wyłączyć proxy: brak REACT_APP_SUPABASE_URL i REACT_APP_SUPABASE_ANON_KEY w .env (lokalnie dodaj też service role do uploadów).'
      );
      return;
    }

    setSaving(true);
    try {
      const dbResult = await persistProxySettingToDatabase(proxyEnabled);

      writeProxyEnabledToStorage(proxyEnabled);

      if (!dbResult.ok) {
        console.warn('Proxy setting DB sync failed:', dbResult.error);
        toast.success(
          proxyEnabled
            ? 'Proxy włączone w tej przeglądarce (odświeżanie). Zapis do bazy nie powiódł się — po przeładowaniu zapisz ponownie, jeśli trzeba.'
            : 'Proxy wyłączone w tej przeglądarce (odświeżanie). Zapis do bazy nie powiódł się.',
          { duration: 5000 }
        );
      } else {
        toast.success(
          proxyEnabled
            ? 'Proxy Supabase włączone — strona się odświeży'
            : 'Proxy Supabase wyłączone — strona się odświeży'
        );
      }

      window.setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      console.error('Error saving supabase proxy config:', err);
      const msg = err?.message || 'nieznany błąd';
      writeProxyEnabledToStorage(proxyEnabled);
      toast.error(
        `Zapis w bazie nie powiódł się (${msg}). Tryb proxy i tak zostanie ustawiony w tej przeglądarce — odświeżam…`,
        { duration: 5000 }
      );
      window.setTimeout(() => window.location.reload(), 800);
    }
  };

  const activeNow = isSupabaseProxyModeActive();
  const proxyEnv = isProxyEnvAvailable();
  const directEnv = isDirectEnvAvailable();

  if (loading) {
    return (
      <div className="manage-tab-panel supabase-proxy-panel" style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: '#000000' }}>Ładowanie ustawień proxy…</p>
      </div>
    );
  }

  return (
    <div className="manage-tab-panel supabase-proxy-panel" style={{ maxWidth: '720px' }}>
      <h1 style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px', color: '#000000' }}>
        <Network size={28} color="#000000" />
        Włącz/wyłącz proxy
      </h1>
      <p style={{ marginBottom: '20px', fontSize: '15px', color: '#000000' }}>
        Steruje tym, czy panel łączy się z Supabase przez <strong>supabase-proxy.php</strong> (produkcja / Vercel),
        czy <strong>bezpośrednio</strong> (np. lokalnie <code>npm start</code> z URL i kluczem w <code>.env</code>).
        Po zapisie strona się przeładuje.
      </p>

      <div className="proxy-status-box" style={{ color: '#000000' }}>
        <div>
          <strong>Aktualnie aktywne:</strong>{' '}
          {activeNow ? 'Proxy (supabase-proxy.php)' : 'Bezpośrednie połączenie z Supabase'}
        </div>
        <ul style={{ margin: '10px 0 0', paddingLeft: '20px' }}>
          <li>Proxy w .env: {proxyEnv ? 'tak' : 'nie'}</li>
          <li>Bezpośrednie (URL + anon) w .env: {directEnv ? 'tak' : 'nie'}</li>
        </ul>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          padding: '20px',
          color: '#000000',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            cursor: proxyEnv ? 'pointer' : 'not-allowed',
            marginBottom: '16px',
            opacity: proxyEnv ? 1 : 0.6,
          }}
        >
          <input
            type="checkbox"
            checked={proxyEnabled}
            disabled={saving || !proxyEnv}
            onChange={(e) => setProxyEnabled(e.target.checked)}
            style={{ width: '20px', height: '20px', marginTop: '2px', cursor: proxyEnv ? 'pointer' : 'not-allowed' }}
          />
          <span style={{ fontWeight: '600', fontSize: '16px', color: '#000000' }}>
            Włącz proxy Supabase (supabase-proxy.php)
          </span>
        </label>

        {!proxyEnv && (
          <p style={{ fontSize: '14px', color: '#b45309', marginBottom: '12px' }}>
            Brak REACT_APP_SUPABASE_PROXY_URL w buildzie — checkbox zablokowany. Na Vercel ustaw ten adres (bez stałego
            tokenu — logowanie przez admin-login.php).
          </p>
        )}

        {!proxyEnabled && (
          <p style={{ fontSize: '14px', color: '#000000', marginBottom: '12px', lineHeight: 1.5 }}>
            <strong>Proxy wyłączone:</strong> panel używa REACT_APP_SUPABASE_URL i REACT_APP_SUPABASE_ANON_KEY. Do
            uploadów z dysku lokalnie dodaj w <code>admin/.env</code> opcjonalnie{' '}
            <code>REACT_APP_SUPABASE_SERVICE_ROLE_KEY</code> (tylko na komputerze, nie na Vercel).
          </p>
        )}

        {proxyEnabled && (
          <p style={{ fontSize: '14px', color: '#000000', marginBottom: '12px', lineHeight: 1.5 }}>
            <strong>Proxy włączone:</strong> zapytania do bazy przez proxy; upload plików do Supabase Storage z panelu
            zwykle nie działa — używaj linków z Storage lub uploadu lokalnego przy wyłączonym proxy.
          </p>
        )}

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Zapisywanie…' : 'Zapisz i przeładuj panel'}
        </button>
      </form>
    </div>
  );
}
